import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Updates from './pages/Updates';
import UpdateDetail from './pages/UpdateDetail';
import Team from './pages/Team';
import TeamDetail from './pages/TeamDetail';
import Children from './pages/Children';
import ChildDetail from './pages/ChildDetail';
import Volunteer from './pages/Volunteer';
import Volunteers from './pages/Volunteers';
import SponsorEvent from './pages/SponsorEvent';
import Donate from './pages/Donate';
import Gallery from './pages/Gallery';
import MessageChairperson from './pages/MessageChairperson';
import OurMission from './pages/OurMission';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminMessages from './pages/admin/AdminMessages';
import AdminChildren from './pages/admin/AdminChildren';
import AdminTeam from './pages/admin/AdminTeam';
import AdminVolunteers from './pages/admin/AdminVolunteers';
import AdminDonors from './pages/admin/AdminDonors';
import AdminGallery from './pages/admin/AdminGallery';
import AdminContent from './pages/admin/AdminContent';
import AdminEngagement from './pages/admin/AdminEngagement';
import AdminSettings from './pages/admin/AdminSettings';
import NotFound from './pages/NotFound';
import './App.css';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const token = localStorage.getItem('adminToken');
  const [isValid, setIsValid] = useState<boolean | null>(null);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setIsValid(false);
        return;
      }

      try {
        const response = await fetch('http://localhost:5000/api/auth/verify', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          setIsValid(false);
          return;
        }

        const data = await response.json();
        if (data?.user) {
          localStorage.setItem('adminUser', JSON.stringify(data.user));
        }
        setIsValid(true);
      } catch {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        setIsValid(false);
      }
    };

    verify();
  }, [token]);

  if (isValid === null) {
    return null;
  }

  return isValid ? children : <Navigate to="/admin/login" replace />;
};

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Admin Login - Public */}
        <Route path="/admin/login" element={<AdminLogin />} />
        
        {/* Admin Routes - Protected */}
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/messages" element={<ProtectedRoute><AdminMessages /></ProtectedRoute>} />
        <Route path="/admin/children" element={<ProtectedRoute><AdminChildren /></ProtectedRoute>} />
        <Route path="/admin/team" element={<ProtectedRoute><AdminTeam /></ProtectedRoute>} />
        <Route path="/admin/volunteers" element={<ProtectedRoute><AdminVolunteers /></ProtectedRoute>} />
        <Route path="/admin/donors" element={<ProtectedRoute><AdminDonors /></ProtectedRoute>} />
        <Route path="/admin/gallery" element={<ProtectedRoute><AdminGallery /></ProtectedRoute>} />
        <Route path="/admin/content" element={<ProtectedRoute><AdminContent /></ProtectedRoute>} />
        <Route path="/admin/engagement" element={<ProtectedRoute><AdminEngagement /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
        
        {/* Public Routes - With Header/Footer */}
        <Route path="/*" element={
          <div className="App">
            <Header />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/about/team" element={<Team />} />
                <Route path="/about/m" element={<MessageChairperson />} />
                <Route path="/about/mission" element={<OurMission />} />
                <Route path="/children" element={<Children />} />
                <Route path="/children/:id" element={<ChildDetail />} />
                <Route path="/projects" element={<Volunteer />} />
                <Route path="/volunteers" element={<Volunteers />} />
                <Route path="/sponsor" element={<SponsorEvent />} />
                <Route path="/donate" element={<Donate />} />
                <Route path="/events" element={<Gallery />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/updates" element={<Updates />} />
                <Route path="/updates/:id" element={<UpdateDetail />} />
                <Route path="/team" element={<Team />} />
                <Route path="/team/:id" element={<TeamDetail />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
          </div>
        } />
      </Routes>
    </Router>
  );
};

export default App;
